import { useState } from "react";
import { Badge, Box, HStack, Switch, Text, VStack } from "@chakra-ui/react";
import ClientService from "@/services/ClientService";
import { showToast } from "@/components/ui/toaster";
import ConfirmDialog from "@/pages/clients/components/ConfirmDialog";

const Counter = ({ label, value }) => (
    <VStack align="flex-start" gap={0} minW="72px">
        <Text color="white" fontWeight={700} fontSize="lg">{value ?? "—"}</Text>
        <Text color="text.grey" fontSize="xs" textTransform="uppercase" letterSpacing="wider">{label}</Text>
    </VStack>
);

/** Compact client summary shown above the tabs: identity, account-active toggle, counters. */
const ClientSummaryHeader = ({ client, counters, onClientChanged }) => {
    const [confirmDeactivate, setConfirmDeactivate] = useState(false);
    const [busy, setBusy] = useState(false);

    const runToggleActive = async (activate) => {
        setBusy(true);
        try {
            await (activate ? ClientService.activateCompany(client.id) : ClientService.deactivateCompany(client.id));
            showToast(activate ? "Konto aktywowane" : "Konto dezaktywowane", null, "success");
            setConfirmDeactivate(false);
            onClientChanged();
        } catch (err) {
            showToast("Błąd", err?.response?.data ?? "Nie udało się zmienić statusu konta", "error");
        } finally {
            setBusy(false);
        }
    };

    const companyTitle = client.companyName?.trim()
        || `${client.firstName ?? ""} ${client.lastName ?? ""}`.trim()
        || "Klient bez nazwy";

    return (
        <>
            <Box w="full" bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={12} p={5}>
                <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={4}>
                    <VStack align="flex-start" gap={2}>
                        <HStack gap={3} align="center" flexWrap="wrap">
                            <Text fontWeight={700} fontSize="xl" color="white">{companyTitle}</Text>
                            <Badge colorPalette={client.isActive ? "green" : "red"} variant="solid">
                                {client.isActive ? "Aktywny" : "Nieaktywny"}
                            </Badge>
                        </HStack>
                        <Text color="text.grey" fontSize="sm">
                            {client.firstName} {client.lastName} · {client.email}
                        </Text>
                        {client.companyNip && <Text color="text.grey" fontSize="xs">NIP {client.companyNip}</Text>}
                    </VStack>
                    <Switch.Root
                        checked={client.isActive}
                        disabled={busy}
                        onCheckedChange={(e) => (e.checked ? runToggleActive(true) : setConfirmDeactivate(true))}
                        colorPalette="green"
                    >
                        <Switch.HiddenInput />
                        <Switch.Control><Switch.Thumb /></Switch.Control>
                        <Switch.Label color="text.grey" fontSize={13}>Konto aktywne</Switch.Label>
                    </Switch.Root>
                </HStack>

                <HStack gap={8} mt={5} pt={4} borderTop="1px solid" borderColor="border.primary" flexWrap="wrap">
                    <Counter label="Scenariusze" value={counters.scenarios} />
                    <Counter label="Modele" value={counters.models} />
                    <Counter label="Rekrutacje" value={counters.recruitments} />
                    <Counter label="Kampanie" value={counters.campaigns} />
                    <Counter label="Interakcje" value={counters.interactions} />
                </HStack>
            </Box>

            <ConfirmDialog
                open={confirmDeactivate}
                title="Dezaktywować konto klienta?"
                body={`Dezaktywacja konta właściciela ${client.firstName} ${client.lastName} wyłącza również wszystkich użytkowników podpiętych do tego klienta.`}
                confirmLabel="Dezaktywuj"
                isLoading={busy}
                onCancel={() => !busy && setConfirmDeactivate(false)}
                onConfirm={() => runToggleActive(false)}
            />
        </>
    );
};

export default ClientSummaryHeader;
